/**
 * Decorates `tern:` links with the live status of the linked issue/task so a
 * spec reads as a progress report. Also parses/links identifiers on demand.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PMNode } from "@tiptap/pm/model";

export type LinkStatus = "backlog" | "unstarted" | "started" | "pending" | "done" | "completed" | "canceled" | "unknown";

export type TernTarget = { kind: "issue"; identifier: string } | { kind: "task"; id: string };

export function parseTernHref(href: string | null | undefined): TernTarget | null {
  if (!href?.startsWith("tern:")) return null;
  const rest = href.slice(5);
  if (rest.startsWith("issue/")) return { kind: "issue", identifier: rest.slice(6).toUpperCase() };
  if (rest.startsWith("task/")) return { kind: "task", id: rest.slice(5) };
  return null;
}

const key = new PluginKey("ternLinkStatus");

export const TernLinkStatus = Extension.create<{ getStatus: (t: TernTarget) => LinkStatus }>({
  name: "ternLinkStatus",
  addOptions() {
    return { getStatus: () => "unknown" as LinkStatus };
  },
  addProseMirrorPlugins() {
    const getStatus = () => this.options.getStatus;
    const build = (doc: PMNode) => {
      const decos: Decoration[] = [];
      doc.descendants((node, pos) => {
        if (!node.isText) return;
        const mark = node.marks.find((m) => m.type.name === "link" && typeof m.attrs.href === "string" && m.attrs.href.startsWith("tern:"));
        if (!mark) return;
        const target = parseTernHref(mark.attrs.href as string);
        if (!target) return;
        const status = getStatus()(target);
        decos.push(Decoration.inline(pos, pos + node.nodeSize, { class: `tern-link tern-link-${status} tern-link-${target.kind}`, "data-tern": mark.attrs.href as string }));
      });
      return DecorationSet.create(doc, decos);
    };
    return [
      new Plugin({
        key,
        state: {
          init: (_, state) => build(state.doc),
          // Rebuild on every transaction: statuses come from outside the doc, so
          // a `refreshTernLinks` meta forces a recompute when the store changes.
          apply: (tr, old, _o, newState) => (tr.docChanged || tr.getMeta("refreshTernLinks") ? build(newState.doc) : old),
        },
        props: { decorations: (state) => key.getState(state) as DecorationSet },
      }),
    ];
  },
});
